import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { colors } from '@getgo/chameleon-core';
import { AlertFilledIcon, PauseCircleOutlinedIcon, PlayCircleOutlinedIcon, RecordCircleOutlinedIcon, StopCircleFilledIcon, } from '@getgo/chameleon-icons/react';
import { Button } from '@getgo/chameleon-web/react';
import Skeleton from '@material-ui/lab/Skeleton';
import classNames from 'classnames';
import { formatDuration } from "../helpers";
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WaveSurfer from 'wavesurfer.js';
import classes from './index.module.scss';
export var AudioPlayerActions;
(function (AudioPlayerActions) {
    AudioPlayerActions[AudioPlayerActions["PLAY"] = 0] = "PLAY";
    AudioPlayerActions[AudioPlayerActions["PAUSE"] = 1] = "PAUSE";
    AudioPlayerActions[AudioPlayerActions["RECORD"] = 2] = "RECORD";
    AudioPlayerActions[AudioPlayerActions["STOP"] = 3] = "STOP";
})(AudioPlayerActions || (AudioPlayerActions = {}));
export const DEFAULT_WAVE_SURFER_PARAMS = {
    barRadius: 2,
    barWidth: 1,
    cursorColor: colors.neutrals.n600,
    cursorWidth: 3,
    height: 22,
    // If true, normalize by the maximum peak instead of 1.0.
    normalize: true,
    // Use the PeakCache to improve rendering speed of large waveforms.
    partialRender: true,
    progressColor: colors.blue.b500,
    responsive: true,
    waveColor: colors.neutrals.n300,
};
export const AUDIO_PLAYER_EMPTY_URL = 'AUDIO_PLAYER_EMPTY_URL';
const AudioPlayer = ({ canRecord = true, className, isRecording, isPlaying, maxRecordingDurationSeconds, onAudioControlClick, onAudioElementCreated, onAudioElementError, onRetry, onWaveSurferError, onWaveSurferReady, onWaveSurferFinish, onWaveSurferSeek, url, waveformContainerClassName, errorContainerClassName, waveSurferParams = {}, peaks, duration, exportPeaks, }) => {
    const { t } = useTranslation();
    const waveformRef = useRef(null);
    const waveSurfer = useRef(null);
    const [audioDuration, setAudioDuration] = React.useState(duration || null);
    const [recordingTime, setRecordingTime] = React.useState(0);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [playing, setPlay] = useState(false);
    const [isLoadingAudioFile, setIsLoadingAudioFile] = useState(!!url);
    const [hasError, setHasError] = useState(false);
    let audioElement;
    const playPause = async () => {
        if (waveSurfer.current) {
            try {
                await waveSurfer.current.playPause();
                setPlay(waveSurfer.current.isPlaying());
            }
            catch (e) {
                setHasError(true);
            }
        }
    };
    // create new WaveSurfer instance
    // On component mount and when url changes
    useEffect(() => {
        if (url && !isRecording) {
            setPlay(false);
            setIsLoadingAudioFile(true);
            waveSurfer.current = WaveSurfer.create(Object.assign(Object.assign(Object.assign({}, DEFAULT_WAVE_SURFER_PARAMS), waveSurferParams), { container: waveformRef.current, backend: 'MediaElement' }));
            audioElement = new Audio(url === AUDIO_PLAYER_EMPTY_URL ? undefined : url);
            audioElement.preload = 'auto';
            audioElement.crossOrigin = 'anonymous';
            /* https://developer.mozilla.org/en-US/docs/Web/API/MediaError*/
            audioElement.addEventListener('error', () => {
                setHasError(true);
                setIsLoadingAudioFile(false);
                onAudioElementError === null || onAudioElementError === void 0 ? void 0 : onAudioElementError(audioElement.error);
            });
            onAudioElementCreated === null || onAudioElementCreated === void 0 ? void 0 : onAudioElementCreated(audioElement);
            // https://wavesurfer-js.org/api/class/src/wavesurfer.js~WaveSurfer.html#instance-method-load
            waveSurfer.current.load(audioElement, peaks, undefined, duration);
            // https://wavesurfer-js.org/docs/events.html
            waveSurfer.current.on('ready', () => {
                setIsLoadingAudioFile(false);
                setAudioDuration(waveSurfer.current.getDuration());
            });
            waveSurfer.current.on('waveform-ready', () => {
                if (!exportPeaks) {
                    return;
                }
                // exportPCM(length: number, accuracy: number, noWindow: boolean, start: number, end: number)
                waveSurfer.current.exportPCM(500, 10000, true).then(peaksJson => {
                    onWaveSurferReady === null || onWaveSurferReady === void 0 ? void 0 : onWaveSurferReady({
                        duration: waveSurfer.current.getDuration(),
                        peaks: JSON.parse(peaksJson),
                    });
                });
            });
            waveSurfer.current.on('seek', () => {
                const currentTime = waveSurfer.current.getCurrentTime();
                setCurrentTime(currentTime);
                onWaveSurferSeek === null || onWaveSurferSeek === void 0 ? void 0 : onWaveSurferSeek(currentTime);
            });
            waveSurfer.current.on('audioprocess', () => {
                setCurrentTime(waveSurfer.current.getCurrentTime());
            });
            waveSurfer.current.on('finish', () => {
                setPlay(false);
                onWaveSurferFinish === null || onWaveSurferFinish === void 0 ? void 0 : onWaveSurferFinish();
            });
            waveSurfer.current.on('error', error => {
                onWaveSurferError === null || onWaveSurferError === void 0 ? void 0 : onWaveSurferError(error);
            });
        }
        // Removes events, elements and disconnects Web Audio nodes.
        // when component unmount
        return () => {
            var _a;
            setAudioDuration(null);
            setHasError(false);
            setCurrentTime(0);
            (_a = waveSurfer.current) === null || _a === void 0 ? void 0 : _a.destroy();
            waveSurfer.current = null;
            audioElement = null;
        };
    }, [url, isRecording]);
    let AudioCtrlIcon = PlayCircleOutlinedIcon;
    let audioCtrLabel = t('Play');
    let audioAction = AudioPlayerActions.PLAY;
    if (!url && !isRecording && canRecord) {
        AudioCtrlIcon = RecordCircleOutlinedIcon;
        audioCtrLabel = t('Record');
        audioAction = AudioPlayerActions.RECORD;
    }
    else if (isRecording) {
        AudioCtrlIcon = StopCircleFilledIcon;
        audioCtrLabel = t('Stop');
        audioAction = AudioPlayerActions.STOP;
    }
    else if (playing) {
        AudioCtrlIcon = PauseCircleOutlinedIcon;
        audioCtrLabel = t('Pause');
        audioAction = AudioPlayerActions.PAUSE;
    }
    const isLoadingUrl = !url && !canRecord;
    const isLoading = isLoadingAudioFile || isLoadingUrl;
    const handleAudioCtrlClick = async () => {
        if (isLoading) {
            return;
        }
        await playPause();
        onAudioControlClick === null || onAudioControlClick === void 0 ? void 0 : onAudioControlClick(audioAction);
    };
    const handleRetry = () => {
        setHasError(false);
        onRetry === null || onRetry === void 0 ? void 0 : onRetry();
    };
    useEffect(() => {
        let recordingInterval;
        if (isRecording) {
            recordingInterval = setInterval(() => setRecordingTime(seconds => seconds + 1), 1000);
        }
        else {
            setRecordingTime(0);
        }
        return () => clearInterval(recordingInterval);
    }, [isRecording]);
    useEffect(() => {
        if (waveSurfer.current) {
            const surferPlaying = waveSurfer.current.isPlaying();
            if ((surferPlaying && isPlaying === false) || (!surferPlaying && isPlaying)) {
                playPause();
            }
        }
    }, [isPlaying]);
    return (_jsxs("div", Object.assign({ className: classNames(classes.audioPlayer, className) }, { children: [hasError && (_jsxs("div", Object.assign({ className: classNames(classes.errorContainer, errorContainerClassName) }, { children: [_jsx(AlertFilledIcon, {}, void 0), _jsx("div", Object.assign({ className: classes.errorContainerLabel }, { children: t('Recording failed to load') }), void 0), _jsx(Button, Object.assign({ size: "small", variant: "secondary", onClick: handleRetry }, { children: t('Retry') }), void 0)] }), void 0)), _jsxs("div", Object.assign({ "data-test-id": "onClickAudioControl", onClick: () => handleAudioCtrlClick(), className: classNames(classes.audioCtrlContainer, isLoading && classes.audioCtrlContainerIsLoading) }, { children: [_jsx("div", Object.assign({ className: classNames(classes.audioCtrlIconContainer, isLoading && classes.audioCtrlIconIsLoading, isRecording && classes.isRecording) }, { children: _jsx(AudioCtrlIcon, {}, void 0) }), void 0), _jsx("div", Object.assign({ className: classes.audioCtrlLabel }, { children: audioCtrLabel }), void 0)] }), void 0), isLoadingUrl ? (_jsx(Skeleton, { animation: "wave", className: classNames(classes.waveformContainerSkeleton) }, void 0)) : (_jsx("div", Object.assign({ className: classNames(classes.waveformContainer, waveformContainerClassName) }, { children: isRecording ? (_jsxs(_Fragment, { children: [_jsx("div", Object.assign({ className: classes.recordingLabel }, { children: t('Recording') }), void 0), _jsxs("div", { children: [formatDuration(recordingTime), !!maxRecordingDurationSeconds &&
                                    ' / ' + formatDuration(maxRecordingDurationSeconds)] }, void 0)] }, void 0)) : (_jsxs(_Fragment, { children: [audioDuration !== null && (_jsx("div", Object.assign({ className: classes.duration }, { children: formatDuration(currentTime) }), void 0)), _jsx("div", { className: classes.waveform, ref: waveformRef }, void 0), audioDuration !== null && (_jsx("div", Object.assign({ className: classes.duration }, { children: formatDuration(audioDuration) }), void 0))] }, void 0)) }), void 0))] }), void 0));
};
export default AudioPlayer;
//# sourceMappingURL=index.js.map